// src/components/App.js
import React, { useState, useEffect } from "react";
import produce from "immer"; // Для immutable updates
import "./style.css";
import MainView from "./components/MainView";
import PayoutsList from "./components/PayoutsList";
import HealthMeasures from "./components/HealthMeasures";
import TSRList from "./components/TSRList";
import SummarizerModal from "./components/SummarizerModal";
import WorkMeasures from "./components/WorkMeasures";
import LawHelp from "./components/LawHelp";
import EducationMeasures from "./components/EducationMeasures";
import SportMeasures from "./components/SportMeasures";
import CultureMeasures from "./components/CultureMeasures";
import { Routes, Route } from 'react-router-dom';

// Custom hook для localStorage (best practice: изолирует логику, auto-sync)
function useLocalStorage(keyPrefix, initialValue) {
  const [state, setState] = useState(() => {
    const loaded = {};
    Object.keys(localStorage)
      .filter((k) => k.startsWith(keyPrefix))
      .forEach((key) => {
        try {
          loaded[key] = JSON.parse(localStorage.getItem(key) || "[]");
        } catch (e) {
          console.error(`Error parsing ${key}:`, e);
          loaded[key] = [];
        }
      });
    return loaded;
  });

  useEffect(() => {
    Object.entries(state).forEach(([key, value]) => {
      localStorage.setItem(key, JSON.stringify(value));
    });
    // Очистка удаленных ключей (если delete в produce)
    Object.keys(localStorage)
      .filter((k) => k.startsWith(keyPrefix) && !(k in state))
      .forEach((key) => localStorage.removeItem(key));
  }, [state, keyPrefix]); // Добавлен keyPrefix в зависимости

  return [state, setState];
}

function App() {
  const [checkedDocs, setCheckedDocs] = useLocalStorage("checklist-", {});
  const [showSummarizer, setShowSummarizer] = useState(false);
  const [summary, setSummary] = useState(null);
  const [globalCheckedDocs, setGlobalCheckedDocs] = useState(new Set());
  const [cachedMeasures, setCachedMeasures] = useState({});

  useEffect(() => {
    // Собираем глобальные checked docs для авто-простановки
    const allDocs = new Set(Object.values(checkedDocs).flat());
    setGlobalCheckedDocs(allDocs);
  }, [checkedDocs]);

  const handleCheckbox = (measureId, docCode, checked) => {
    setCheckedDocs(
      produce((draft) => {
        if (checked) {
          const key = `checklist-${measureId}`;
          if (!draft[key]) draft[key] = [];
          if (!draft[key].includes(docCode)) draft[key].push(docCode);
        } else {
          Object.keys(draft).forEach((key) => {
            if (draft[key]) {
              draft[key] = draft[key].filter((code) => code !== docCode);
              if (draft[key].length === 0) delete draft[key];
            }
          });
        }
      })
    );
  };

  const loadMeasures = async (categoryCode) => {
    if (cachedMeasures[categoryCode]) {
      return cachedMeasures[categoryCode];
    }
    try {
      const res = await fetch(
        `/api/measures/?category__code=${categoryCode}`
      );
      if (!res.ok) throw new Error("Ошибка загрузки");
      const data = await res.json();
      setCachedMeasures((prev) => ({ ...prev, [categoryCode]: data }));
      return data;
    } catch (err) {
      console.error("Ошибка:", err);
      alert("Не удалось загрузить меры поддержки. Попробуйте позже.");
      return [];
    }
  };

  const analyzeDocs = async () => {
    const allDocCodes = [...globalCheckedDocs];
    if (!allDocCodes.length) return { error: "No documents selected" };

    try {
      const response = await fetch(
        `/api/summarize/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ doc_codes: allDocCodes }),
        }
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSummary(data);
      return data;
    } catch (error) {
      console.error("Error analyzing documents:", error);
      return { error: error.message };
    }
  };

  return (
  <>
    <Routes>
  <Route path="/" element={<MainView />} />

  {/* Социальные выплаты */}
  <Route path="/payouts" element={
    <PayoutsList 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="socvyplaty"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/payouts/:measureId" element={
    <PayoutsList 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="socvyplaty"
      loadMeasures={loadMeasures}
    />
  } />

  {/* Образование */}
  <Route path="/edu" element={
    <EducationMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="education"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/edu/:measureId" element={
    <EducationMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="education"
      loadMeasures={loadMeasures}
    />
  } />

  {/* Здравоохранение */}
  <Route path="/health" element={
    <HealthMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="health"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/health/:measureId" element={
    <HealthMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="health"
      loadMeasures={loadMeasures}
    />
  } />

  {/* === НОВЫЕ ДИНАМИЧЕСКИЕ КАТЕГОРИИ === */}
  {/* Спорт */}
  <Route path="/sport" element={
    <SportMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="sport"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/sport/:measureId" element={
    <SportMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="sport"
      loadMeasures={loadMeasures}
    />
  } />

  {/* Труд и занятость */}
  <Route path="/work" element={
    <WorkMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="work"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/work/:measureId" element={
    <WorkMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="work"
      loadMeasures={loadMeasures}
    />
  } />

  {/* Юридическая помощь */}
  <Route path="/law" element={
    <LawHelp 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="law"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/law/:measureId" element={
    <LawHelp 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="law"
      loadMeasures={loadMeasures}
    />
  } />

  {/* Культура и туризм */}
  <Route path="/culture" element={
    <CultureMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="culture"
      loadMeasures={loadMeasures}
    />
  } />
  <Route path="/culture/:measureId" element={
    <CultureMeasures 
      setCheckedDocs={setCheckedDocs}
      handleCheckbox={handleCheckbox}
      checkedDocs={checkedDocs}
      globalCheckedDocs={globalCheckedDocs}
      categoryCode="culture"
      loadMeasures={loadMeasures}
    />
  } />

  {/* TSR остаётся без чек-листов */}
  <Route path="/tsr" element={<TSRList />} />

  <Route path="*" element={<div className="text-center py-5">404: Страница не найдена</div>} />
</Routes>

    {/* Кнопка саммаризатора и модалка */}
    <button
      className="summarizer-btn"
      onClick={() => setShowSummarizer(true)}
      title="Что ещё я могу получить?"
    >
      {/* SVG */}
    </button>

    {showSummarizer && (
      <SummarizerModal
        isOpen={showSummarizer}
        onClose={() => {
          setShowSummarizer(false);
          setSummary(null);
        }}
        onAnalyze={analyzeDocs}
        summary={summary}
      />
    )}
  </>
);
}

export default App;
